/**
 * Number formatting for the UI.
 *
 * Strategy:
 *   - < 1000: integer, locale-grouped ("12 487")
 *   - < 1M:   one decimal "K" ("12.5K")
 *   - < 1B:   one decimal "M"
 *   - < 1T:   one decimal "B"
 *   - else:   scientific "1.2e15"
 *
 * Locale: `ru-RU` uses a non-breaking thin space as the group separator,
 * which matches the canon ("12 487" in `docs/09` §VII.2).
 */

const RU_NUMBER_FORMATTER = new Intl.NumberFormat('ru-RU', {
  maximumFractionDigits: 0,
});

const SCALE_THRESHOLDS = [
  { divisor: 1_000_000_000_000, suffix: 'T' },
  { divisor: 1_000_000_000, suffix: 'B' },
  { divisor: 1_000_000, suffix: 'M' },
  { divisor: 1_000, suffix: 'K' },
] as const;

function formatScaled(value: number): string {
  // value is in the range [1, 1000) at the time we format with the suffix
  if (value >= 100) {
    return Math.floor(value).toString();
  }
  if (value >= 10) {
    return value.toFixed(1);
  }
  return value.toFixed(2);
}

export function formatNumber(n: number): string {
  if (!Number.isFinite(n)) {
    return '∞';
  }
  const abs = Math.abs(n);

  if (abs < 1000) {
    return RU_NUMBER_FORMATTER.format(Math.floor(n));
  }

  for (const { divisor, suffix } of SCALE_THRESHOLDS) {
    if (abs >= divisor) {
      // Promote to the next scale if rounding would otherwise emit "1000K"
      const scaled = n / divisor;
      if (scaled >= 1000) {
        continue;
      }
      return `${formatScaled(scaled)}${suffix}`;
    }
  }

  return n.toExponential(2);
}

export function formatMultiplier(m: number): string {
  if (!Number.isFinite(m)) {
    return '×∞';
  }
  if (m < 10) {
    return `×${m.toFixed(2)}`;
  }
  if (m < 100) {
    return `×${m.toFixed(1)}`;
  }
  return `×${formatNumber(m)}`;
}
